import React from 'react';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { ExternalLink, X } from 'lucide-react';

import { Flex, FlexColumn } from 'src/components/flex';

import styles from 'src/pages/landlord/leads/messages/components/input/ReplySuggestions.module.css';

type Props = {
    propertyType: string | null;
    website: string | null;
    onSetUp: () => void;
    onDismiss: () => void;
};

/**
 * Shown when a tour request is detected but the property has no tour availability
 * configured, so no slots can be suggested. Points the landlord at the setup flow via
 * an inline link instead of silently showing nothing. When the property has a website
 * fact, also offers it as a place to look up the hours.
 */
export const TourAvailabilityPlaceholder = ({
    propertyType,
    website,
    onSetUp,
    onDismiss,
}: Props) => {
    const subject = propertyType ? `this ${propertyType.toLowerCase()}` : 'this property';

    return (
        <Flex
            columnGap={2}
            alignItems="flex-start"
            justifyContent="space-between"
            width="100%"
            data-testid="tour-availability-placeholder"
        >
            <FlexColumn rowGap={0.5}>
                <Typography variant="subtitle2" component="div">
                    {`Tour request detected, but no tour availability is set for ${subject}.`}
                </Typography>
                <div className={styles.placeholderText}>
                    <span
                        className={styles.link}
                        data-testid="set-up-tour-availability"
                        onClick={onSetUp}
                    >
                        Click here
                    </span>
                    {' to set up tour availability and use the auto-suggested tour slots.'}
                    {website && (
                        <>
                            {' Go to the website to get the hours: '}
                            <a
                                href={website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.link}
                                data-testid="tour-availability-website"
                            >
                                {website}
                                <ExternalLink size={12} />
                            </a>
                        </>
                    )}
                </div>
            </FlexColumn>
            <IconButton
                size="small"
                color="error"
                data-testid="dismiss-tour-availability-placeholder"
                onClick={onDismiss}
            >
                <X size={18} />
            </IconButton>
        </Flex>
    );
};

export default TourAvailabilityPlaceholder;
